import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AI_LABEL_CORNERS, AI_LABEL_KINDS, AI_LABEL_SIZES, AI_LABEL_VARIANTS, createAiLabelPlacement } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';
import PanelBody from '../../components/panel-body/PanelBody';
import PanelSection from '../../components/panel-section/PanelSection';

import scss from './AiLabelsPanel.module.scss';

function AiLabelsPanel({ options, onChangeOptions }) {
  const { t } = useTranslation('mediaStudio');
  const { editor, notice } = useMediaStudio();

  const addLabel = (kind, variantId) =>
    notice.run(
      () =>
        editor.addImage(kind.variants[variantId], {
          name: t(kind.nameKey),
          getPlacement: createAiLabelPlacement(kind, options),
        }),
      'loadFailed',
    );

  const setOption = (key, value) => onChangeOptions({ ...options, [key]: value });

  return (
    <PanelBody>
      <PanelSection title={t('aiLabelSize')} help={t('aiLabelSizeHint')}>
        <div className={scss.segmented} role='radiogroup' aria-label={t('aiLabelSize')}>
          {AI_LABEL_SIZES.map((size) => (
            <button
              key={size.id}
              type='button'
              role='radio'
              aria-checked={options.size === size.id}
              className={clsx(scss.segment, options.size === size.id && scss.segmentActive)}
              onClick={() => setOption('size', size.id)}>
              {t(size.nameKey)}
            </button>
          ))}
        </div>
      </PanelSection>

      <PanelSection title={t('aiLabelCorner')} help={t('aiLabelCornerHint')}>
        <div className={scss.corners} role='radiogroup' aria-label={t('aiLabelCorner')}>
          {AI_LABEL_CORNERS.map((corner) => (
            <button
              key={corner.id}
              type='button'
              role='radio'
              aria-checked={options.corner === corner.id}
              title={t(corner.nameKey)}
              aria-label={t(corner.nameKey)}
              className={clsx(scss.corner, options.corner === corner.id && scss.cornerActive)}
              onClick={() => setOption('corner', corner.id)}>
              <span className={clsx(scss.cornerDot, scss[corner.id])} />
            </button>
          ))}
        </div>
      </PanelSection>

      {AI_LABEL_KINDS.map((kind) => (
        <PanelSection key={kind.id} title={t(kind.nameKey)} help={t(kind.hintKey)}>
          <div className={clsx(scss.variants, kind.id === 'basic' && scss.variantsCompact)}>
            {AI_LABEL_VARIANTS.map((variant) => {
              const label = `${t(kind.nameKey)}, ${t(variant.nameKey)}`;
              return (
                <button
                  key={variant.id}
                  type='button'
                  className={scss.variant}
                  title={label}
                  aria-label={label}
                  onClick={() => addLabel(kind, variant.id)}>
                  <img src={kind.variants[variant.id]} alt='' />
                </button>
              );
            })}
          </div>
        </PanelSection>
      ))}
    </PanelBody>
  );
}

export default AiLabelsPanel;
